import React, { useState, useRef, useEffect, useCallback } from 'react';

/**
 * BUDDY FLOW EDITOR - Multi-Conversation Visual Editor
 * Visual drag-and-drop editor for Darija Buddy conversation flows
 * Like Dialogflow - drag nodes, connect them, build multiple conversations
 * 
 * Features:
 * - LEFT SIDEBAR: List of conversations to create/select/delete
 * - Drag & drop nodes on canvas
 * - Mouse wheel zoom
 * - Full screen mode
 * - Conditional logic (if/else)
 * - Time controls
 */

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Node Types for Buddy Bot
const NODE_TYPES = {
  START: 'start',
  MESSAGE: 'message',
  USER_RESPONSE: 'user_response',
  TIP: 'tip',
  IMAGE: 'image',
  CONDITION: 'condition',
  DELAY: 'delay',
  END: 'end'
};

// Node colors and icons
const NODE_COLORS = {
  [NODE_TYPES.START]: { bg: '#10B981', border: '#059669', icon: '▶️', label: 'Start' },
  [NODE_TYPES.MESSAGE]: { bg: '#3B82F6', border: '#2563EB', icon: '💬', label: 'Bot Message' },
  [NODE_TYPES.USER_RESPONSE]: { bg: '#F59E0B', border: '#D97706', icon: '👤', label: 'User Response' },
  [NODE_TYPES.TIP]: { bg: '#8B5CF6', border: '#7C3AED', icon: '📚', label: 'Show Tip' },
  [NODE_TYPES.IMAGE]: { bg: '#EC4899', border: '#DB2777', icon: '🖼️', label: 'Show Image' },
  [NODE_TYPES.CONDITION]: { bg: '#F97316', border: '#EA580C', icon: '🔀', label: 'If/Else' },
  [NODE_TYPES.DELAY]: { bg: '#6366F1', border: '#4F46E5', icon: '⏱️', label: 'Delay' },
  [NODE_TYPES.END]: { bg: '#EF4444', border: '#DC2626', icon: '🔚', label: 'End' }
};

// Generate unique ID
const generateId = () => `node_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// Default node data
const getDefaultNodeData = (type) => {
  switch (type) {
    case NODE_TYPES.START:
      return { label: 'Conversation Start', name: 'start', text: 'Salam! 👋' };
    case NODE_TYPES.MESSAGE:
      return { 
        text: 'Bot message here...',
        textAlt: [],
        speaker: 'bot',
        name: 'message_' + Date.now(),
        hasOptions: false,
        options: []
      };
    case NODE_TYPES.USER_RESPONSE:
      return {
        prompt: 'User chooses:',
        name: 'response_' + Date.now(),
        options: [
          { id: generateId(), text: 'Option 1', nextNodeId: null },
          { id: generateId(), text: 'Option 2', nextNodeId: null }
        ]
      };
    case NODE_TYPES.TIP:
      return {
        tipType: 'random',
        category: null,
        specificTipId: null,
        name: 'tip_' + Date.now(),
        options: [
          { id: generateId(), text: 'Nice! 👍', nextNodeId: null },
          { id: generateId(), text: 'More!', nextNodeId: null }
        ]
      };
    case NODE_TYPES.CONDITION:
      return {
        name: 'condition_' + Date.now(),
        conditionType: 'tips_seen',
        operator: 'equals',
        value: '0',
        trueBranch: null,
        falseBranch: null
      };
    case NODE_TYPES.IMAGE:
      return {
        name: 'image_' + Date.now(),
        imageSource: 'specific', // 'specific' or 'random'
        imageUrl: '',
        caption: '',
        category: null, // for random mode
        options: [
          { id: generateId(), text: 'Nice! 😍', nextNodeId: null },
          { id: generateId(), text: 'Show more!', nextNodeId: null }
        ]
      };
    case NODE_TYPES.DELAY:
      return {
        name: 'delay_' + Date.now(),
        delaySeconds: 2,
        nextNodeId: null
      };
    case NODE_TYPES.END:
      return { 
        message: 'See you later! 👋',
        autoClose: true,
        name: 'end',
        closeDelay: 2
      };
    default:
      return {};
  }
};

const BuddyFlowEditor = ({ API, adminToken, isDarkMode, showNotification, categories = [], tips = [] }) => {
  // ===== MULTI-CONVERSATION STATE =====
  const [conversations, setConversations] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [showNewConversationModal, setShowNewConversationModal] = useState(false);
  const [newConversationTitle, setNewConversationTitle] = useState('');
  const [editingTitle, setEditingTitle] = useState(null);
  
  // ===== IMAGE LIBRARY STATE =====
  const [imageCategories, setImageCategories] = useState([]);
  const [libraryImages, setLibraryImages] = useState([]);
  
  // ===== CURRENT FLOW STATE =====
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  // ===== CANVAS STATE =====
  const canvasRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 50, y: 50 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [draggingNode, setDraggingNode] = useState(null);
  const [connecting, setConnecting] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  
  // ===== UI STATE =====
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [flowSettings, setFlowSettings] = useState({
    tipsPerDay: 6,
    testMode: true,
    testIntervalSeconds: 60,
    intervalMinutes: [5, 10, 15, 20, 25, 30],
    requireGreeting: true
  });

  // Get active conversation
  const activeConversation = conversations.find(c => c.id === activeConversationId);

  // ===== MOUSE WHEEL ZOOM =====
  const handleWheel = useCallback((e) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      setZoom(z => Math.max(0.3, Math.min(2.5, z + delta)));
    } else {
      setPan(p => ({
        x: p.x - e.deltaX * 0.5,
        y: p.y - e.deltaY * 0.5
      }));
    }
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      canvas.addEventListener('wheel', handleWheel, { passive: false });
      return () => canvas.removeEventListener('wheel', handleWheel);
    }
  }, [handleWheel]);

  // Escape key to exit fullscreen
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen]);

  // ===== SELECT CONVERSATION =====
  const selectConversation = useCallback((conv) => {
    setActiveConversationId(conv.id);
    setNodes(conv.nodes || []);
    setEdges(conv.edges || []);
    setHasChanges(false);
    setSelectedNode(null);
    setPan({ x: 50, y: 50 });
    setZoom(1);
  }, []);

  // ===== CREATE DEFAULT CONVERSATION =====
  const createDefaultConversation = useCallback(() => {
    const startNode = {
      id: generateId(),
      type: NODE_TYPES.START,
      position: { x: 300, y: 50 },
      data: { label: 'Start', name: 'start', text: 'Salam! 👋' }
    };
    
    const responseNode = {
      id: generateId(),
      type: NODE_TYPES.USER_RESPONSE,
      position: { x: 250, y: 180 },
      data: {
        name: 'greeting_response',
        prompt: 'How do you respond?',
        options: [
          { id: generateId(), text: 'Wa3alikom salam!', nextNodeId: null },
          { id: generateId(), text: 'Salam!', nextNodeId: null }
        ]
      }
    };
    
    const defaultConv = {
      id: generateId(),
      title: 'Greeting Flow 1',
      isActive: true,
      nodes: [startNode, responseNode],
      edges: [{ id: `edge_${startNode.id}_${responseNode.id}`, source: startNode.id, target: responseNode.id }]
    };
    
    setConversations([defaultConv]);
    setActiveConversationId(defaultConv.id);
    setNodes(defaultConv.nodes);
    setEdges(defaultConv.edges);
    setHasChanges(false);
    setSelectedNode(null);
  }, []);

  // ===== FETCH CONVERSATIONS ON MOUNT =====
  const fetchConversations = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await fetch(`${API}/admin/buddy/conversations`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        const convs = data.conversations || [];
        setConversations(convs);
        
        // Load settings
        if (data.settings) {
          setFlowSettings(prev => ({
            ...prev,
            ...data.settings
          }));
        }
        
        // Select first conversation if exists
        if (convs.length > 0) {
          selectConversation(convs[0]);
        } else {
          createDefaultConversation();
        }
      } else {
        // Fallback: create default
        createDefaultConversation();
      }
    } catch (err) {
      console.error('Failed to fetch conversations:', err);
      createDefaultConversation();
    }
    setIsLoading(false);
  }, [API, adminToken, selectConversation, createDefaultConversation]);

  useEffect(() => {
    fetchConversations();
    fetchImageLibrary();
  }, [fetchConversations]);

  // ===== FETCH IMAGE LIBRARY =====
  const fetchImageLibrary = async () => {
    try {
      const [catsRes, imagesRes] = await Promise.all([
        fetch(`${API}/admin/buddy/image-categories`, {
          headers: { 'Authorization': `Bearer ${adminToken}` }
        }),
        fetch(`${API}/admin/buddy/images`, {
          headers: { 'Authorization': `Bearer ${adminToken}` }
        })
      ]);
      if (catsRes.ok) setImageCategories(await catsRes.json());
      if (imagesRes.ok) setLibraryImages(await imagesRes.json());
    } catch (err) {
      console.error('Failed to fetch image library:', err);
    }
  };

  // ===== CREATE CONVERSATION =====
  const createConversation = () => {
    const title = newConversationTitle.trim() || `Conversation ${conversations.length + 1}`;
    const convId = generateId();
    
    const startNode = {
      id: generateId(),
      type: NODE_TYPES.START,
      position: { x: 300, y: 50 },
      data: { label: 'Start', name: 'start', text: 'Salam! 👋' }
    };
    
    const newConv = {
      id: convId,
      title: title,
      isActive: true,
      nodes: [startNode],
      edges: []
    };
    
    const updatedConvs = [...conversations, newConv];
    setConversations(updatedConvs);
    selectConversation(newConv);
    setNewConversationTitle('');
    setShowNewConversationModal(false);
    setHasChanges(true);
  };

  // ===== DELETE CONVERSATION =====
  const deleteConversation = (convId) => {
    if (conversations.length <= 1) {
      showNotification('Cannot delete the last conversation', 'error');
      return;
    }
    if (!window.confirm('Delete this conversation?')) return;
    
    const newConvs = conversations.filter(c => c.id !== convId);
    setConversations(newConvs);
    
    if (activeConversationId === convId) {
      selectConversation(newConvs[0]);
    }
    setHasChanges(true);
  };

  // ===== TOGGLE CONVERSATION ACTIVE =====
  const toggleConversationActive = (convId) => {
    setConversations(prev => prev.map(c => 
      c.id === convId ? { ...c, isActive: !c.isActive } : c
    ));
    setHasChanges(true);
  };

  // ===== AUTO LAYOUT - Arrange nodes left-to-right, top-to-bottom =====
  const autoLayoutNodes = () => {
    if (nodes.length === 0) return;
    
    // Build adjacency map
    const adjacencyMap = {};
    const inDegree = {};
    
    nodes.forEach(n => {
      adjacencyMap[n.id] = [];
      inDegree[n.id] = 0;
    });
    
    // Count edges from explicit edges
    edges.forEach(e => {
      if (adjacencyMap[e.source]) {
        adjacencyMap[e.source].push(e.target);
        inDegree[e.target] = (inDegree[e.target] || 0) + 1;
      }
    });
    
    // Count edges from options
    nodes.forEach(n => {
      (n.data?.options || []).forEach(opt => {
        if (opt.nextNodeId && adjacencyMap[n.id] && !adjacencyMap[n.id].includes(opt.nextNodeId)) {
          adjacencyMap[n.id].push(opt.nextNodeId);
          inDegree[opt.nextNodeId] = (inDegree[opt.nextNodeId] || 0) + 1;
        }
      });
    });
    
    // Find start node (0 incoming edges or type === 'start')
    let startNode = nodes.find(n => n.type === 'start');
    if (!startNode) {
      startNode = nodes.find(n => inDegree[n.id] === 0);
    }
    if (!startNode) {
      startNode = nodes[0];
    }
    
    // BFS to assign levels (left to right)
    const levels = {};
    const visited = new Set();
    const queue = [{ id: startNode.id, level: 0 }];
    
    while (queue.length > 0) {
      const { id, level } = queue.shift();
      if (visited.has(id)) continue;
      visited.add(id);
      levels[id] = level;
      
      (adjacencyMap[id] || []).forEach(nextId => {
        if (!visited.has(nextId)) {
          queue.push({ id: nextId, level: level + 1 });
        }
      });
    }
    
    // Include any unvisited nodes
    nodes.forEach(n => {
      if (!visited.has(n.id)) {
        levels[n.id] = 0;
      }
    });
    
    // Group by level
    const levelGroups = {};
    Object.entries(levels).forEach(([nodeId, level]) => {
      if (!levelGroups[level]) levelGroups[level] = [];
      levelGroups[level].push(nodeId);
    });
    
    // Position nodes: TOP-TO-BOTTOM by level, left-to-right within level
    const NODE_WIDTH = 200;
    const NODE_HEIGHT = 120;
    const H_SPACING = 220;  // Horizontal spacing within level
    const V_SPACING = 150;  // Vertical spacing between levels
    const START_X = 300;
    const START_Y = 50;
    
    const newPositions = {};
    Object.entries(levelGroups).forEach(([level, nodeIds]) => {
      nodeIds.forEach((nodeId, index) => {
        newPositions[nodeId] = {
          x: START_X + index * H_SPACING,  // Spread horizontally within same level
          y: START_Y + parseInt(level) * V_SPACING  // Stack vertically by level
        };
      });
    });
    
    // Update node positions
    setNodes(prev => prev.map(n => ({
      ...n,
      position: newPositions[n.id] || n.position
    })));
    
    // Reset pan and zoom to fit
    setPan({ x: 50, y: 50 });
    setZoom(0.9);
    setHasChanges(true);
    showNotification('Nodes auto-arranged!', 'success');
  };

  // ===== RENAME CONVERSATION =====
  const renameConversation = (convId, newTitle) => {
    setConversations(prev => prev.map(c => 
      c.id === convId ? { ...c, title: newTitle } : c
    ));
    setEditingTitle(null);
    setHasChanges(true);
  };

  // ===== DUPLICATE CONVERSATION =====
  const duplicateConversation = (conv) => {
    const newId = generateId();
    // Deep clone nodes with new IDs
    const idMap = {};
    const newNodes = conv.nodes.map(n => {
      const newNodeId = generateId();
      idMap[n.id] = newNodeId;
      return {
        ...n,
        id: newNodeId,
        data: {
          ...n.data,
          options: (n.data?.options || []).map(opt => ({
            ...opt,
            id: generateId()
          }))
        }
      };
    });
    
    // Update edges with new IDs
    const newEdges = conv.edges.map(e => ({
      ...e,
      id: generateId(),
      source: idMap[e.source] || e.source,
      target: idMap[e.target] || e.target
    }));
    
    // Update option nextNodeIds
    newNodes.forEach(n => {
      if (n.data?.options) {
        n.data.options = n.data.options.map(opt => ({
          ...opt,
          nextNodeId: idMap[opt.nextNodeId] || opt.nextNodeId
        }));
      }
    });
    
    const duplicatedConv = {
      id: newId,
      title: `${conv.title} (Copy)`,
      isActive: false,
      nodes: newNodes,
      edges: newEdges
    };
    
    setConversations(prev => [...prev, duplicatedConv]);
    setHasChanges(true);
    showNotification('Conversation duplicated!', 'success');
  };

  // ===== UPDATE CURRENT CONVERSATION IN LIST =====
  const updateCurrentConversation = useCallback(() => {
    if (!activeConversationId) return;
    setConversations(prev => prev.map(c => 
      c.id === activeConversationId ? { ...c, nodes, edges } : c
    ));
  }, [activeConversationId, nodes, edges]);

  // Sync nodes/edges changes back to conversations list
  useEffect(() => {
    if (activeConversationId && (nodes.length > 0 || edges.length > 0)) {
      updateCurrentConversation();
    }
  }, [nodes, edges, activeConversationId, updateCurrentConversation]);

  // ===== ADD NODE =====
  const addNode = (type) => {
    if (!activeConversationId) {
      showNotification('Select or create a conversation first', 'error');
      return;
    }
    
    const newNode = {
      id: generateId(),
      type,
      position: { x: 200 + nodes.length * 30, y: 200 + nodes.length * 30 },
      data: getDefaultNodeData(type)
    };
    
    setNodes(prev => [...prev, newNode]);
    setSelectedNode(newNode);
    setHasChanges(true);
    
    // Refresh categories when adding an IMAGE node to get latest names
    if (type === NODE_TYPES.IMAGE) {
      fetchImageLibrary();
    }
  };

  // ===== DELETE NODE =====
  const deleteNode = (nodeId) => {
    setNodes(prev => prev.filter(n => n.id !== nodeId));
    setEdges(prev => prev.filter(e => e.source !== nodeId && e.target !== nodeId));
    setSelectedNode(null);
    setHasChanges(true);
  };

  // ===== ADD EDGE =====
  const addEdge = (sourceId, targetId, branch = null) => {
    const edgeId = `edge_${sourceId}_${targetId}${branch ? '_' + branch : ''}`;
    if (edges.find(e => e.id === edgeId)) return;
    
    setEdges(prev => [...prev, { id: edgeId, source: sourceId, target: targetId, branch }]);
    setHasChanges(true);
  };

  // ===== UPDATE NODE DATA =====
  const updateNodeData = (nodeId, newData) => {
    setNodes(prev => prev.map(n => 
      n.id === nodeId ? { ...n, data: { ...n.data, ...newData } } : n
    ));
    // Also update selectedNode if it's the one being edited
    if (selectedNode && selectedNode.id === nodeId) {
      setSelectedNode(prev => ({
        ...prev,
        data: { ...prev.data, ...newData }
      }));
    }
    setHasChanges(true);
  };

  // ===== UPDATE NODE POSITION =====
  const updateNodePosition = (nodeId, position) => {
    setNodes(prev => prev.map(n =>
      n.id === nodeId ? { ...n, position } : n
    ));
  };

  // ===== CANVAS MOUSE HANDLERS =====
  const handleCanvasMouseDown = (e) => {
    if (e.target === canvasRef.current || e.target.tagName === 'svg') {
      setIsPanning(true);
      setPanStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
      setSelectedNode(null);
    }
  };

  const handleCanvasMouseMove = useCallback((e) => {
    if (isPanning) {
      setPan({ x: e.clientX - panStart.x, y: e.clientY - panStart.y });
    }
    
    if (draggingNode) {
      const rect = canvasRef.current?.getBoundingClientRect();
      if (rect) {
        const newX = (e.clientX - rect.left - pan.x) / zoom;
        const newY = (e.clientY - rect.top - pan.y) / zoom;
        updateNodePosition(draggingNode.id, { 
          x: newX - draggingNode.offsetX, 
          y: newY - draggingNode.offsetY 
        });
      }
    }
    
    if (connecting) {
      setConnecting(prev => ({ ...prev, x: e.clientX, y: e.clientY }));
    }
  }, [isPanning, panStart, draggingNode, connecting, zoom, pan]);

  const handleCanvasMouseUp = () => {
    setIsPanning(false);
    if (draggingNode) {
      setDraggingNode(null);
      setHasChanges(true);
    }
    if (connecting) {
      setConnecting(null);
    }
  };

  // ===== SAVE ALL =====
  const saveAll = async () => {
    setIsSaving(true);
    try {
      // Update current conversation with latest nodes/edges
      const updatedConversations = conversations.map(c => 
        c.id === activeConversationId ? { ...c, nodes, edges } : c
      );
      
      const formData = new FormData();
      formData.append('conversations', JSON.stringify(updatedConversations));
      formData.append('settings', JSON.stringify(flowSettings));
      
      const res = await fetch(`${API}/admin/buddy/conversations/bulk`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${adminToken}` },
        body: formData
      });
      
      if (res.ok) {
        showNotification('All conversations saved!', 'success');
        setHasChanges(false);
      } else {
        showNotification('Failed to save', 'error');
      }
    } catch (err) {
      showNotification('Error saving', 'error');
    }
    setIsSaving(false);
  };

  // ===== RENDER NODE =====
  const renderNode = (node) => {
    const colors = NODE_COLORS[node.type] || NODE_COLORS[NODE_TYPES.MESSAGE];
    const isSelected = selectedNode?.id === node.id;
    
    return (
      <div
        key={node.id}
        className={`absolute rounded-xl shadow-lg cursor-move transition-all ${isSelected ? 'ring-4 ring-yellow-400' : ''}`}
        style={{
          left: node.position.x * zoom + pan.x,
          top: node.position.y * zoom + pan.y,
          backgroundColor: colors.bg,
          borderColor: colors.border,
          borderWidth: '3px',
          borderStyle: 'solid',
          minWidth: '180px',
          maxWidth: '240px',
          transform: `scale(${zoom})`,
          transformOrigin: 'top left',
          zIndex: isSelected ? 100 : (draggingNode?.id === node.id ? 100 : 10)
        }}
        onClick={(e) => {
          e.stopPropagation();
          setSelectedNode(node);
          // Refresh categories when selecting an IMAGE node to get latest names
          if (node.type === NODE_TYPES.IMAGE) {
            fetchImageLibrary();
          }
        }}
        onMouseDown={(e) => {
          if (e.button === 0) {
            e.stopPropagation();
            const rect = e.currentTarget.getBoundingClientRect();
            setDraggingNode({
              id: node.id,
              offsetX: (e.clientX - rect.left) / zoom,
              offsetY: (e.clientY - rect.top) / zoom
            });
          }
        }}
      >
        <div className="p-3">
          {/* Node header */}
          <div className="flex items-center gap-2 mb-2">
            <span className="text-lg">{colors.icon}</span>
            <span className="text-white font-bold text-xs">{colors.label}</span>
            {node.type === NODE_TYPES.START && (
              <span className="ml-auto px-1.5 py-0.5 bg-white/30 text-white text-xs rounded">START</span>
            )}
          </div>
          
          {/* Node content preview */}
          <div className="text-white text-xs opacity-90 break-words">
            {(node.type === NODE_TYPES.MESSAGE || node.type === NODE_TYPES.START) && (
              <p className="truncate">{node.data.text || '...'}</p>
            )}
            {node.type === NODE_TYPES.USER_RESPONSE && (
              <div>
                <p className="text-xs opacity-70 mb-1">Responses:</p>
                {node.data.options?.slice(0, 2).map((opt, i) => (
                  <p key={i} className="truncate text-xs">• {opt.text}</p>
                ))}
                {node.data.options?.length > 2 && (
                  <p className="text-xs opacity-60">+{node.data.options.length - 2} more</p>
                )}
              </div>
            )}
            {node.type === NODE_TYPES.TIP && (
              <p>📚 Show {node.data.tipType === 'random' ? 'random' : node.data.category} tip</p>
            )}
            {node.type === NODE_TYPES.IMAGE && (
              <div>
                <p>🖼️ {node.data.imageSource === 'random' ? 'Random image' : 'Show image'}</p>
                {node.data.caption && <p className="opacity-70 truncate text-xs">{node.data.caption}</p>}
              </div>
            )}
            {node.type === NODE_TYPES.CONDITION && (
              <div>
                <p>If {node.data.conditionType}</p>
                <p className="opacity-70">{node.data.operator} &quot;{node.data.value}&quot;</p>
              </div>
            )}
            {node.type === NODE_TYPES.DELAY && (
              <p>⏱️ Wait {node.data.delaySeconds}s</p>
            )}
            {node.type === NODE_TYPES.END && (
              <p className="truncate">{node.data.message || 'End'}</p>
            )}
          </div>
        </div>
        
        {/* Output connection point - BOTTOM */}
        {node.type === NODE_TYPES.CONDITION ? (
          <>
            <div
              className="absolute -bottom-3 left-1/4 transform -translate-x-1/2 w-4 h-4 bg-green-400 rounded-full border-2 border-green-600 cursor-crosshair hover:scale-125 transition-all flex items-center justify-center text-xs"
              title="True"
              onMouseDown={(e) => {
                e.stopPropagation();
                setConnecting({ sourceId: node.id, branch: 'true', x: e.clientX, y: e.clientY });
              }}
            >✓</div>
            <div
              className="absolute -bottom-3 left-3/4 transform -translate-x-1/2 w-4 h-4 bg-red-400 rounded-full border-2 border-red-600 cursor-crosshair hover:scale-125 transition-all flex items-center justify-center text-xs"
              title="False"
              onMouseDown={(e) => {
                e.stopPropagation();
                setConnecting({ sourceId: node.id, branch: 'false', x: e.clientX, y: e.clientY });
              }}
            >✗</div>
          </>
        ) : node.type !== NODE_TYPES.END && (
          <div
            className="absolute -bottom-3 left-1/2 transform -translate-x-1/2 w-4 h-4 bg-white rounded-full border-2 border-gray-500 cursor-crosshair hover:bg-yellow-300 hover:scale-125 transition-all flex items-center justify-center text-xs"
            onMouseDown={(e) => {
              e.stopPropagation();
              setConnecting({ sourceId: node.id, x: e.clientX, y: e.clientY });
            }}
          >↓</div>
        )}
        
        {/* Input connection point - TOP */}
        {node.type !== NODE_TYPES.START && (
          <div
            className="absolute -top-3 left-1/2 transform -translate-x-1/2 w-4 h-4 bg-white rounded-full border-2 border-gray-500 flex items-center justify-center text-xs"
            onMouseUp={() => {
              if (connecting && connecting.sourceId !== node.id) {
                addEdge(connecting.sourceId, node.id, connecting.branch);
                setConnecting(null);
              }
            }}
          >↑</div>
        )}
      </div>
    );
  };

  // ===== RENDER EDGES =====
  const renderEdges = () => {
    // Collect all edges: explicit edges + option connections
    const allEdges = [...edges];
    
    // Add edges from node options (User Response, Tip nodes)
    nodes.forEach(node => {
      const options = node.data?.options || [];
      options.forEach((opt, idx) => {
        if (opt.nextNodeId) {
          const edgeId = `opt_${node.id}_${opt.nextNodeId}_${idx}`;
          // Only add if not already in edges
          if (!allEdges.find(e => e.source === node.id && e.target === opt.nextNodeId)) {
            allEdges.push({
              id: edgeId,
              source: node.id,
              target: opt.nextNodeId,
              isOption: true,
              optionText: opt.text
            });
          }
        }
      });
    });
    
    return (
      <svg className="absolute inset-0 pointer-events-none" style={{ overflow: 'visible', zIndex: 5 }}>
        <defs>
          {/* Bigger arrowheads for better visibility */}
          <marker id="arrowhead" markerWidth="14" markerHeight="10" refX="12" refY="5" orient="auto">
            <polygon points="0 0, 14 5, 0 10" fill="#6B7280" />
          </marker>
          <marker id="arrowhead-green" markerWidth="14" markerHeight="10" refX="12" refY="5" orient="auto">
            <polygon points="0 0, 14 5, 0 10" fill="#10B981" />
          </marker>
          <marker id="arrowhead-red" markerWidth="14" markerHeight="10" refX="12" refY="5" orient="auto">
            <polygon points="0 0, 14 5, 0 10" fill="#EF4444" />
          </marker>
          <marker id="arrowhead-purple" markerWidth="14" markerHeight="10" refX="12" refY="5" orient="auto">
            <polygon points="0 0, 14 5, 0 10" fill="#8B5CF6" />
          </marker>
          <marker id="arrowhead-orange" markerWidth="14" markerHeight="10" refX="12" refY="5" orient="auto">
            <polygon points="0 0, 14 5, 0 10" fill="#F59E0B" />
          </marker>
          <marker id="arrowhead-blue" markerWidth="14" markerHeight="10" refX="12" refY="5" orient="auto">
            <polygon points="0 0, 14 5, 0 10" fill="#3B82F6" />
          </marker>
        </defs>
        
        {allEdges.map((edge, edgeIndex) => {
          const sourceNode = nodes.find(n => n.id === edge.source);
          const targetNode = nodes.find(n => n.id === edge.target);
          
          if (!sourceNode || !targetNode) return null;
          
          // Calculate offset and styling based on edge type
          let xOffset = 90;  // Default center
          let strokeColor = edge.isOption ? '#F59E0B' : '#6B7280';
          let marker = edge.isOption ? 'url(#arrowhead-orange)' : 'url(#arrowhead)';
          let strokeWidth = 3;  // Thicker lines
          let dashArray = edge.isOption ? '8,4' : 'none';
          
          if (edge.branch === 'true') {
            xOffset = 45;
            strokeColor = '#10B981';
            marker = 'url(#arrowhead-green)';
          } else if (edge.branch === 'false') {
            xOffset = 135;
            strokeColor = '#EF4444';
            marker = 'url(#arrowhead-red)';
          }
          
          // Spread out multiple connections from same source
          const sameSourceEdges = allEdges.filter(e => e.source === edge.source);
          if (sameSourceEdges.length > 1 && edge.isOption) {
            const idx = sameSourceEdges.indexOf(edge);
            xOffset = 50 + (idx * 40);
          }
          
          // VERTICAL FLOW: Connect from BOTTOM to TOP
          const x1 = sourceNode.position.x * zoom + pan.x + xOffset;
          const y1 = sourceNode.position.y * zoom + pan.y + 90;  // Bottom of source
          const x2 = targetNode.position.x * zoom + pan.x + 90;  // Center of target
          const y2 = targetNode.position.y * zoom + pan.y - 5;   // Top of target
          
          const midY = (y1 + y2) / 2;
          
          return (
            <g key={edge.id}>
              {/* Main path - VERTICAL curve (top to bottom) */}
              <path
                d={`M ${x1} ${y1} C ${x1} ${midY}, ${x2} ${midY}, ${x2} ${y2}`}
                fill="none"
                stroke={strokeColor}
                strokeWidth={strokeWidth}
                strokeDasharray={dashArray}
                markerEnd={marker}
                opacity={0.9}
              />
              {/* Edge label for option text */}
              {edge.isOption && edge.optionText && (
                <g>
                  <rect
                    x={(x1 + x2) / 2 - 35}
                    y={midY - 10}
                    width="70"
                    height="16"
                    rx="4"
                    fill="rgba(245, 158, 11, 0.15)"
                    stroke="#F59E0B"
                    strokeWidth="1"
                  />
                  <text
                    x={(x1 + x2) / 2}
                    y={midY + 2}
                    fill="#F59E0B"
                    fontSize="9"
                    fontWeight="600"
                    textAnchor="middle"
                  >
                    {edge.optionText.length > 12 ? edge.optionText.substring(0, 10) + '...' : edge.optionText}
                  </text>
                </g>
              )}
              {/* Branch labels (YES/NO) */}
              {edge.branch && (
                <g>
                  <rect
                    x={(x1 + x2) / 2 - 20}
                    y={midY - 12}
                    width="40"
                    height="18"
                    rx="4"
                    fill={edge.branch === 'true' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)'}
                    stroke={edge.branch === 'true' ? '#10B981' : '#EF4444'}
                    strokeWidth="1"
                  />
                  <text
                    x={(x1 + x2) / 2}
                    y={midY}
                    fill={edge.branch === 'true' ? '#10B981' : '#EF4444'}
                    fontSize="11"
                    fontWeight="bold"
                    textAnchor="middle"
                  >
                    {edge.branch === 'true' ? 'YES' : 'NO'}
                  </text>
                </g>
              )}
            </g>
          );
        })}
        
        {/* Drawing connection line - VERTICAL */}
        {connecting && canvasRef.current && (
          <line
            x1={nodes.find(n => n.id === connecting.sourceId)?.position.x * zoom + pan.x + (connecting.branch === 'true' ? 45 : connecting.branch === 'false' ? 135 : 90) || 0}
            y1={nodes.find(n => n.id === connecting.sourceId)?.position.y * zoom + pan.y + 90 || 0}
            x2={connecting.x - canvasRef.current.getBoundingClientRect().left}
            y2={connecting.y - canvasRef.current.getBoundingClientRect().top}
            stroke={connecting.branch === 'true' ? '#10B981' : connecting.branch === 'false' ? '#EF4444' : '#F59E0B'}
            strokeWidth="3"
            strokeDasharray="8,4"
          />
        )}
      </svg>
    );
  };

  // ===== RENDER NODE EDITOR PANEL =====
  const renderNodeEditor = () => {
    if (!selectedNode) return null;
    
    const colors = NODE_COLORS[selectedNode.type];
    
    return (
      <div className={`w-72 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-xl p-4 overflow-y-auto max-h-[80vh]`}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="text-lg">{colors.icon}</span>
            <span className={`font-bold text-sm ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{colors.label}</span>
          </div>
          <div className="flex gap-1">
            {selectedNode.type !== NODE_TYPES.START && (
              <button onClick={() => deleteNode(selectedNode.id)} className="p-1 text-red-500 hover:bg-red-50 rounded text-sm">🗑</button>
            )}
            <button onClick={() => setSelectedNode(null)} className="text-gray-400 hover:text-gray-600 text-lg">✕</button>
          </div>
        </div>
        
        {/* Node name */}
        <div className="mb-3">
          <label className={`block text-xs mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Node Name</label>
          <input
            type="text"
            value={selectedNode.data.name || ''}
            onChange={(e) => updateNodeData(selectedNode.id, { name: e.target.value })}
            className={`w-full px-2 py-1.5 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
            placeholder="e.g. greeting"
          />
        </div>
        
        {/* Message nodes */}
        {(selectedNode.type === NODE_TYPES.MESSAGE || selectedNode.type === NODE_TYPES.START) && (
          <>
            <div className="mb-3">
              <label className={`block text-xs mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Bot Message</label>
              <textarea
                value={selectedNode.data.text || ''}
                onChange={(e) => updateNodeData(selectedNode.id, { text: e.target.value })}
                className={`w-full px-2 py-1.5 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                rows={2}
              />
            </div>
            <div className="mb-3">
              <label className={`block text-xs mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Alt Messages (one per line)</label>
              <textarea
                value={(selectedNode.data.textAlt || []).join('\n')}
                onChange={(e) => {
                  // Split by newlines but keep empty lines to allow Enter key
                  const lines = e.target.value.split('\n');
                  updateNodeData(selectedNode.id, { textAlt: lines });
                }}
                onBlur={(e) => {
                  // Clean up empty lines only on blur (when user leaves the field)
                  const cleanedLines = e.target.value.split('\n').filter(t => t.trim());
                  updateNodeData(selectedNode.id, { textAlt: cleanedLines });
                }}
                onKeyDown={(e) => {
                  // Stop propagation to prevent any parent handlers from intercepting Enter
                  e.stopPropagation();
                }}
                className={`w-full px-2 py-1.5 rounded-lg border text-sm resize-y ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                style={{ minHeight: '120px' }}
                rows={8}
              />
              <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                💡 Add multiple variations. Bot randomly picks one each conversation.
              </p>
            </div>

            {/* Response Options Toggle for MESSAGE node */}
            <div className="mb-3">
              <div className="flex items-center justify-between">
                <label className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  Add Response Options?
                </label>
                <button
                  onClick={() => {
                    const hasOpts = selectedNode.data.hasOptions;
                    updateNodeData(selectedNode.id, { 
                      hasOptions: !hasOpts,
                      options: !hasOpts ? [
                        { id: generateId(), text: 'Yes!', nextNodeId: null },
                        { id: generateId(), text: 'No thanks', nextNodeId: null }
                      ] : []
                    });
                  }}
                  className={`px-3 py-1 rounded-lg text-xs font-medium ${
                    selectedNode.data.hasOptions
                      ? 'bg-blue-600 text-white'
                      : isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
                  }`}
                >
                  {selectedNode.data.hasOptions ? '✓ Yes' : 'No'}
                </button>
              </div>
              <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                💡 Add buttons like "Yes/No" for user to respond
              </p>
            </div>

            {/* Show Connect to Next only if NO response options */}
            {!selectedNode.data.hasOptions && (
              <div className="mb-3">
                <label className={`block text-xs mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>➡️ Connect to Next Node</label>
                <select
                  value={edges.find(e => e.source === selectedNode.id)?.target || ''}
                  onChange={(e) => {
                    // Remove old edge from this node
                    const newEdges = edges.filter(edge => edge.source !== selectedNode.id);
                    // Add new edge if selected
                    if (e.target.value) {
                      newEdges.push({
                        id: `edge_${selectedNode.id}_${e.target.value}`,
                        source: selectedNode.id,
                        target: e.target.value
                      });
                    }
                    setEdges(newEdges);
                    setHasChanges(true);
                  }}
                  className={`w-full px-2 py-1.5 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                >
                  <option value="">-- Select next node --</option>
                  {nodes.filter(n => n.id !== selectedNode.id).map(n => (
                    <option key={n.id} value={n.id}>
                      {NODE_COLORS[n.type]?.icon} {n.data.name || n.type}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </>
        )}
        
        {/* Response Options for USER_RESPONSE, TIP, IMAGE, and MESSAGE (when hasOptions) */}
        {(selectedNode.type === NODE_TYPES.USER_RESPONSE || selectedNode.type === NODE_TYPES.TIP || selectedNode.type === NODE_TYPES.IMAGE || (selectedNode.type === NODE_TYPES.MESSAGE && selectedNode.data.hasOptions)) && (
          <div className="mb-3">
            <label className={`block text-xs mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Response Options</label>
            <div className="space-y-2">
              {(selectedNode.data.options || []).map((opt, idx) => (
                <div key={opt.id} className={`p-2 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <input
                    type="text"
                    value={opt.text}
                    onChange={(e) => {
                      const newOptions = [...selectedNode.data.options];
                      newOptions[idx] = { ...newOptions[idx], text: e.target.value };
                      updateNodeData(selectedNode.id, { options: newOptions });
                    }}
                    className={`w-full px-2 py-1 rounded border text-xs mb-1 ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                    placeholder="Button text"
                  />
                  <div className="flex items-center gap-1">
                    <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>→</span>
                    <select
                      value={opt.nextNodeId || ''}
                      onChange={(e) => {
                        const newOptions = [...selectedNode.data.options];
                        newOptions[idx] = { ...newOptions[idx], nextNodeId: e.target.value || null };
                        updateNodeData(selectedNode.id, { options: newOptions });
                      }}
                      className={`flex-1 px-1 py-0.5 rounded border text-xs ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                    >
                      <option value="">🔚 End conversation</option>
                      {nodes.filter(n => n.id !== selectedNode.id).map(n => (
                        <option key={n.id} value={n.id}>
                          {NODE_COLORS[n.type]?.icon} {n.data.name || n.type}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={() => {
                        const newOptions = selectedNode.data.options.filter((_, i) => i !== idx);
                        updateNodeData(selectedNode.id, { options: newOptions });
                      }}
                      className="text-red-500 text-xs"
                    >✕</button>
                  </div>
                </div>
              ))}
              <button
                onClick={() => {
                  const newOptions = [...(selectedNode.data.options || []), { id: generateId(), text: 'New option', nextNodeId: null }];
                  updateNodeData(selectedNode.id, { options: newOptions });
                }}
                className="w-full px-2 py-1 border-2 border-dashed border-gray-400 rounded-lg text-xs text-gray-500 hover:border-purple-500"
              >+ Add Option</button>
            </div>
          </div>
        )}
        
        {/* Tip node specific */}
        {selectedNode.type === NODE_TYPES.TIP && (
          <div className="mb-3">
            <label className={`block text-xs mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Tip Type</label>
            <select
              value={selectedNode.data.tipType || 'random'}
              onChange={(e) => updateNodeData(selectedNode.id, { tipType: e.target.value })}
              className={`w-full px-2 py-1.5 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
            >
              <option value="random">Random (any)</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.name}>{cat.emoji} {cat.label}</option>
              ))}
            </select>
          </div>
        )}

        {/* Image node specific */}
        {selectedNode.type === NODE_TYPES.IMAGE && (
          <>
            <div className="mb-3">
              <label className={`block text-xs mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Image Source</label>
              <div className="flex gap-2">
                <button
                  onClick={() => updateNodeData(selectedNode.id, { imageSource: 'specific' })}
                  className={`flex-1 px-2 py-1.5 rounded-lg text-xs font-medium ${
                    (selectedNode.data.imageSource || 'specific') === 'specific'
                      ? 'bg-pink-600 text-white'
                      : isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
                  }`}
                >
                  🔗 Specific URL
                </button>
                <button
                  onClick={() => updateNodeData(selectedNode.id, { imageSource: 'random' })}
                  className={`flex-1 px-2 py-1.5 rounded-lg text-xs font-medium ${
                    selectedNode.data.imageSource === 'random'
                      ? 'bg-pink-600 text-white'
                      : isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
                  }`}
                >
                  🎲 Random
                </button>
              </div>
            </div>

            {(selectedNode.data.imageSource || 'specific') === 'specific' ? (
              <>
                <div className="mb-3">
                  <label className={`block text-xs mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Image URL (Dropbox)</label>
                  <input
                    type="text"
                    value={selectedNode.data.imageUrl || ''}
                    onChange={(e) => updateNodeData(selectedNode.id, { imageUrl: e.target.value })}
                    placeholder="https://www.dropbox.com/..."
                    className={`w-full px-2 py-1.5 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                  />
                </div>
                <div className="mb-3">
                  <label className={`block text-xs mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Caption</label>
                  <input
                    type="text"
                    value={selectedNode.data.caption || ''}
                    onChange={(e) => updateNodeData(selectedNode.id, { caption: e.target.value })}
                    placeholder="Traditional Moroccan Kaftan"
                    className={`w-full px-2 py-1.5 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                  />
                </div>
                {selectedNode.data.imageUrl && (
                  <div className="mb-3">
                    <p className={`text-xs mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Preview:</p>
                    <img
                      src={selectedNode.data.imageUrl.replace('?dl=0', '?raw=1').replace('www.dropbox.com', 'dl.dropboxusercontent.com')}
                      alt="Preview"
                      className="w-full h-24 object-cover rounded-lg"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                )}
              </>
            ) : (
              <div className="mb-3">
                <label className={`block text-xs mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Pick from Category</label>
                <select
                  value={selectedNode.data.category || ''}
                  onChange={(e) => updateNodeData(selectedNode.id, { category: e.target.value })}
                  className={`w-full px-2 py-1.5 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                >
                  <option value="">Select category...</option>
                  {imageCategories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.emoji} {cat.name}</option>
                  ))}
                </select>
                <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  💡 Bot will randomly pick an image from this category
                </p>
              </div>
            )}
          </>
        )}
        
        {/* Delay node specific */}
        {selectedNode.type === NODE_TYPES.DELAY && (
          <div className="mb-3">
            <label className={`block text-xs mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Delay (seconds)</label>
            <input
              type="number"
              min="1"
              max="60"
              value={selectedNode.data.delaySeconds || 2}
              onChange={(e) => updateNodeData(selectedNode.id, { delaySeconds: parseInt(e.target.value) || 2 })}
              className={`w-full px-2 py-1.5 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
            />
          </div>
        )}
        
        {/* End node specific */}
        {selectedNode.type === NODE_TYPES.END && (
          <div className="mb-3">
            <label className={`block text-xs mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Goodbye Message</label>
            <input
              type="text"
              value={selectedNode.data.message || ''}
              onChange={(e) => updateNodeData(selectedNode.id, { message: e.target.value })}
              className={`w-full px-2 py-1.5 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
            />
          </div>
        )}
        
        {/* Condition node specific */}
        {selectedNode.type === NODE_TYPES.CONDITION && (
          <>
            <div className="mb-3">
              <label className={`block text-xs mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Condition</label>
              <select
                value={selectedNode.data.conditionType || 'tips_seen'}
                onChange={(e) => updateNodeData(selectedNode.id, { conditionType: e.target.value })}
                className={`w-full px-2 py-1.5 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
              >
                <option value="tips_seen">Tips seen today</option>
                <option value="random">Random (50/50)</option>
              </select>
            </div>
            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Connect TRUE (✓) and FALSE (✗) outputs below
            </p>
          </>
        )}
      </div>
    );
  };

  // ===== MAIN RENDER =====
  if (isLoading) {
    return (
      <div className={`flex items-center justify-center h-96 ${isDarkMode ? 'bg-gray-900' : 'bg-gray-100'}`}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  const editorContent = (
    <div className={`flex h-full ${isFullscreen ? 'fixed inset-0 z-50' : ''} ${isDarkMode ? 'bg-gray-900' : 'bg-gray-100'}`}>
      {/* ===== LEFT SIDEBAR - CONVERSATION LIST ===== */}
      <div className={`w-64 flex-shrink-0 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} border-r ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} flex flex-col`}>
        {/* Header */}
        <div className={`p-3 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="flex items-center justify-between">
            <h3 className={`font-bold text-sm ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              📋 Conversations
            </h3>
            <button
              onClick={() => setShowNewConversationModal(true)}
              className="px-2 py-1 bg-purple-600 text-white rounded hover:bg-purple-700 text-xs flex items-center gap-1"
              title="New Conversation"
            >+ New</button>
          </div>
        </div>
        
        {/* Conversation List */}
        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {conversations.map(conv => (
            <div
              key={conv.id}
              onClick={() => selectConversation(conv)}
              className={`p-2 rounded-lg cursor-pointer transition-all ${
                activeConversationId === conv.id
                  ? 'bg-purple-600 text-white'
                  : isDarkMode
                    ? 'hover:bg-gray-700 text-gray-300'
                    : 'hover:bg-gray-100 text-gray-700'
              }`}
            >
              <div className="flex items-center justify-between">
                {editingTitle === conv.id ? (
                  <input
                    type="text"
                    defaultValue={conv.title}
                    autoFocus
                    onClick={(e) => e.stopPropagation()}
                    onBlur={(e) => renameConversation(conv.id, e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') renameConversation(conv.id, e.target.value);
                      if (e.key === 'Escape') setEditingTitle(null);
                    }}
                    className={`flex-1 px-1 py-0.5 text-xs rounded ${isDarkMode ? 'bg-gray-600 text-white' : 'bg-white text-gray-800'}`}
                  />
                ) : (
                  <span className="text-xs font-medium truncate flex-1" onDoubleClick={(e) => { e.stopPropagation(); setEditingTitle(conv.id); }}>
                    {conv.title}
                  </span>
                )}
                <div className="flex items-center gap-1 ml-1">
                  <button
                    onClick={(e) => { e.stopPropagation(); setEditingTitle(conv.id); }}
                    className={`text-xs opacity-60 hover:opacity-100 ${activeConversationId === conv.id ? 'text-purple-200' : 'text-gray-500'}`}
                    title="Rename"
                  >✏️</button>
                  <button
                    onClick={(e) => { e.stopPropagation(); duplicateConversation(conv); }}
                    className={`text-xs opacity-60 hover:opacity-100 ${activeConversationId === conv.id ? 'text-purple-200' : 'text-gray-500'}`}
                    title="Duplicate"
                  >📋</button>
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleConversationActive(conv.id); }}
                    className={`w-4 h-4 rounded text-xs ${conv.isActive ? 'bg-green-500 text-white' : 'bg-gray-400 text-white'}`}
                    title={conv.isActive ? 'Active' : 'Inactive'}
                  >
                    {conv.isActive ? '✓' : '−'}
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); deleteConversation(conv.id); }}
                    className={`text-xs ${activeConversationId === conv.id ? 'text-red-200 hover:text-red-100' : 'text-red-400 hover:text-red-600'}`}
                    title="Delete"
                  >🗑</button>
                </div>
              </div>
              <div className={`text-xs mt-1 ${activeConversationId === conv.id ? 'text-purple-200' : isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                {conv.nodes?.length || 0} nodes
              </div>
            </div>
          ))}
          
          {conversations.length === 0 && (
            <div className={`text-center py-8 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              <p className="text-3xl mb-2">🌳</p>
              <p className="text-xs">No conversations yet</p>
              <button
                onClick={() => setShowNewConversationModal(true)}
                className="mt-2 text-xs text-purple-500 hover:underline"
              >Create one</button>
            </div>
          )}
        </div>
        
        {/* Settings Button */}
        <div className={`p-2 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <button
            onClick={() => setShowSettings(true)}
            className={`w-full px-3 py-2 rounded-lg text-xs flex items-center justify-center gap-2 ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            ⚙️ Settings
          </button>
        </div>
      </div>
      
      {/* ===== MAIN CANVAS AREA ===== */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Toolbar */}
        <div className={`px-4 py-2 border-b ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'} flex items-center justify-between`}>
          <div className="flex items-center gap-2">
            <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              {activeConversation?.title || 'Select a conversation'}
            </span>
            {hasChanges && <span className="text-xs text-orange-500">• Unsaved</span>}
          </div>
          
          <div className="flex items-center gap-2">
            {/* Node buttons */}
            {activeConversationId && (
              <div className="flex gap-1">
                {Object.entries(NODE_TYPES).filter(([k]) => k !== 'START').map(([key, type]) => (
                  <button
                    key={key}
                    onClick={() => addNode(type)}
                    className={`px-2 py-1 rounded text-xs ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-100 hover:bg-gray-200'}`}
                    title={`Add ${NODE_COLORS[type].label}`}
                  >
                    {NODE_COLORS[type].icon}
                  </button>
                ))}
              </div>
            )}
            
            <div className="w-px h-6 bg-gray-300 mx-2"></div>
            
            {/* Zoom controls */}
            <button onClick={() => setZoom(z => Math.max(0.3, z - 0.2))} className={`p-1 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200'}`}>➖</button>
            <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{Math.round(zoom * 100)}%</span>
            <button onClick={() => setZoom(z => Math.min(2.5, z + 0.2))} className={`p-1 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200'}`}>➕</button>
            <button onClick={() => { setZoom(1); setPan({ x: 50, y: 50 }); }} className={`px-2 py-1 text-xs rounded ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'}`}>Reset</button>
            
            {/* Auto Layout button */}
            {activeConversationId && (
              <button 
                onClick={autoLayoutNodes}
                className={`px-2 py-1 text-xs rounded flex items-center gap-1 ${isDarkMode ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-blue-500 hover:bg-blue-600 text-white'}`}
                title="Auto-arrange nodes left-to-right"
              >
                📐 Auto Layout
              </button>
            )}
            
            <div className="w-px h-6 bg-gray-300 mx-2"></div>
            
            {/* Fullscreen toggle */}
            <button
              onClick={() => setIsFullscreen(!isFullscreen)}
              className={`p-1 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200'}`}
              title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
            >
              {isFullscreen ? '⛶' : '⛶'}
            </button>
            
            {/* Save button */}
            <button
              onClick={saveAll}
              disabled={isSaving || !hasChanges}
              className={`px-3 py-1 rounded text-sm font-medium ${
                hasChanges
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : isDarkMode ? 'bg-gray-700 text-gray-500' : 'bg-gray-200 text-gray-400'
              }`}
            >
              {isSaving ? '...' : '💾 Save All'}
            </button>
          </div>
        </div>
        
        {/* Canvas */}
        <div className="flex-1 flex">
          <div
            ref={canvasRef}
            className={`flex-1 relative overflow-hidden cursor-grab ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}
            style={{ 
              backgroundImage: isDarkMode 
                ? 'radial-gradient(circle, #374151 1px, transparent 1px)' 
                : 'radial-gradient(circle, #D1D5DB 1px, transparent 1px)',
              backgroundSize: `${20 * zoom}px ${20 * zoom}px`,
              backgroundPosition: `${pan.x}px ${pan.y}px`
            }}
            onMouseDown={handleCanvasMouseDown}
            onMouseMove={handleCanvasMouseMove}
            onMouseUp={handleCanvasMouseUp}
            onMouseLeave={handleCanvasMouseUp}
          >
            {renderEdges()}
            {nodes.map(renderNode)}
            
            {!activeConversationId && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className={`text-center ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  <p className="text-5xl mb-4">👈</p>
                  <p className="text-lg">Select a conversation from the left</p>
                  <p className="text-sm">or create a new one</p>
                </div>
              </div>
            )}
          </div>
          
          {/* Node Editor Panel */}
          {selectedNode && (
            <div className={`border-l ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              {renderNodeEditor()}
            </div>
          )}
        </div>
      </div>
      
      {/* ===== NEW CONVERSATION MODAL ===== */}
      {showNewConversationModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className={`w-96 rounded-xl p-6 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              New Conversation Flow
            </h3>
            <input
              type="text"
              value={newConversationTitle}
              onChange={(e) => setNewConversationTitle(e.target.value)}
              placeholder="e.g. Greeting Flow 2, Joke of the Day..."
              className={`w-full px-4 py-3 rounded-lg border mb-4 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
              autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter') createConversation(); }}
            />
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => { setShowNewConversationModal(false); setNewConversationTitle(''); }}
                className={`px-4 py-2 rounded-lg ${isDarkMode ? 'bg-gray-700 text-white' : 'bg-gray-200'}`}
              >Cancel</button>
              <button
                onClick={createConversation}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >Create</button>
            </div>
          </div>
        </div>
      )}
      
      {/* ===== SETTINGS MODAL ===== */}
      {showSettings && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className={`w-[500px] rounded-xl p-6 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              ⚙️ Bot Settings
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Tips Per Day</label>
                <input
                  type="number"
                  min="1"
                  max="20"
                  value={flowSettings.tipsPerDay}
                  onChange={(e) => setFlowSettings(prev => ({ ...prev, tipsPerDay: parseInt(e.target.value) || 6 }))}
                  className={`w-full px-3 py-2 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                />
              </div>
              
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={flowSettings.testMode}
                  onChange={(e) => setFlowSettings(prev => ({ ...prev, testMode: e.target.checked }))}
                  className="w-4 h-4 rounded"
                />
                <div>
                  <span className={`${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Test Mode</span>
                  <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Bot appears every {flowSettings.testIntervalSeconds}s instead of random intervals
                  </p>
                </div>
              </label>
              
              {flowSettings.testMode && (
                <div>
                  <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Test Interval (seconds)</label>
                  <input
                    type="number"
                    min="10"
                    max="300"
                    value={flowSettings.testIntervalSeconds}
                    onChange={(e) => setFlowSettings(prev => ({ ...prev, testIntervalSeconds: parseInt(e.target.value) || 60 }))}
                    className={`w-full px-3 py-2 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                  />
                </div>
              )}
            </div>
            
            <div className="flex gap-3 justify-end mt-6">
              <button
                onClick={() => setShowSettings(false)}
                className={`px-4 py-2 rounded-lg ${isDarkMode ? 'bg-gray-700 text-white' : 'bg-gray-200'}`}
              >Close</button>
              <button
                onClick={() => { setHasChanges(true); setShowSettings(false); }}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >Apply</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className={`${isFullscreen ? '' : 'rounded-xl overflow-hidden'}`} style={{ height: isFullscreen ? '100vh' : '600px' }}>
      {editorContent}
    </div>
  );
};

export default BuddyFlowEditor;
